import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import {
  usePromotionsCreateMutation,
  usePromotionsDestroyMutation,
  usePromotionsListQuery,
  usePromotionsApplyToGroupMutation,
  usePromotionsPartialUpdateMutation,
} from '../../../services/promotionsApi';
import { useSalonsMySalonsRetrieveQuery } from '../../../services/salonsApi';
import PromotionBanner from '../../common/PromotionBanner';
import {
  useSalonsCategoriesListQuery,
  useServicesSalonListQuery,
  useUsersClientsBySalonListQuery,
} from '../../../services/GLOWClient';
import theme from '../../../theme';

const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const fromDatetimeLocal = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const defaultForm = {
  apply_scope: 'current',
  selected_salon_ids: [],
  name: '',
  description: '',
  is_active: true,
  starts_at: '',
  ends_at: '',
  is_stackable: false,
  priority: 0,
  max_uses_total: '',
  max_uses_per_client: '',
  banner_enabled: false,
  banner_message: '',
  banner_style: 'info',
  banner_priority: 0,
  audience_type: 'all_clients',
  audience_clients: [],
  apply_to_all_services: true,
  categories: [],
  services: [],
  rule_type: 'percent_off',
  percent: 10,
  nth: 3,
};

const SalonPromotions = ({ salonId, categories = [], services = [] }) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = usePromotionsListQuery();
  const [createPromotion, { isLoading: isCreating }] = usePromotionsCreateMutation();
  const [updatePromotion, { isLoading: isUpdating }] = usePromotionsPartialUpdateMutation();
  const [applyToGroup, { isLoading: isApplyingToGroup }] = usePromotionsApplyToGroupMutation();
  const [deletePromotion, { isLoading: isDeleting }] = usePromotionsDestroyMutation();

  const { data: mySalonsData } = useSalonsMySalonsRetrieveQuery(undefined);
  const mySalons = useMemo(() => {
    if (Array.isArray(mySalonsData)) return mySalonsData;
    if (Array.isArray(mySalonsData?.results)) return mySalonsData.results;
    if (mySalonsData && typeof mySalonsData === 'object') {
      const id = mySalonsData?.salon_id ?? mySalonsData?.id ?? mySalonsData?.pk;
      if (id != null) return [mySalonsData];
    }
    return [];
  }, [mySalonsData]);

  const canChooseSalon = mySalons.length > 1;
  const hasAnySalon = mySalons.length > 0;

  useEffect(() => {
    console.log('[SalonPromotions] mounted', { salonId });
  }, [salonId]);

  const { data: categoriesData, error: categoriesError } = useSalonsCategoriesListQuery(
    { salonId: Number(salonId) },
    { skip: !salonId }
  );

  const { data: servicesData, error: servicesError } = useServicesSalonListQuery(
    { salonId: Number(salonId) },
    { skip: !salonId }
  );

  const { data: clientsData, isLoading: clientsLoading, error: clientsError } = useUsersClientsBySalonListQuery(
    { salonId: Number(salonId) },
    { skip: !salonId }
  );

  const clients = Array.isArray(clientsData) ? clientsData : (clientsData?.results || []);

  const effectiveCategories = useMemo(() => {
    if (Array.isArray(categories) && categories.length > 0) return categories;
    return Array.isArray(categoriesData) ? categoriesData : (categoriesData?.results || []);
  }, [categories, categoriesData]);

  const effectiveServices = useMemo(() => {
    if (Array.isArray(services) && services.length > 0) return services;
    return Array.isArray(servicesData) ? servicesData : (servicesData?.results || []);
  }, [services, servicesData]);

  const salonPromotions = useMemo(() => {
    const items = Array.isArray(data) ? data : (data?.results || []);
    const sid = Number(salonId);
    return items.filter((p) => Number(p?.salon) === sid);
  }, [data, salonId]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [submitError, setSubmitError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    { label: t('pro_promotions.step_details'), icon: '📝' },
    { label: t('pro_promotions.step_offer'), icon: '🎁' },
    { label: t('pro_promotions.step_conditions'), icon: '⚙️' },
    { label: t('pro_promotions.step_audience'), icon: '👥' },
    { label: t('pro_promotions.step_visibility'), icon: '📢' }
  ];

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  const [duplicateSalonIds, setDuplicateSalonIds] = useState([]);
  const [duplicateError, setDuplicateError] = useState('');
  const [duplicateOverrides, setDuplicateOverrides] = useState({
    name: '',
    description: '',
    starts_at: '',
    ends_at: '',
  });

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...defaultForm,
      apply_scope: 'current',
      selected_salon_ids: salonId ? [Number(salonId)] : [],
    });
    setSubmitError('');
    setActiveStep(0);
    setDialogOpen(true);
  };

  const openEdit = (promo) => {
    setEditing(promo);
    setSubmitError('');
    setActiveStep(0);
    setForm({
      ...defaultForm,
      apply_to_all_salons: false,
      selected_salon_ids: promo?.salon != null ? [promo.salon] : [],
      name: promo?.name || '',
      description: promo?.description || '',
      is_active: !!promo?.is_active,
      starts_at: toDatetimeLocal(promo?.starts_at),
      ends_at: toDatetimeLocal(promo?.ends_at),
      is_stackable: !!promo?.is_stackable,
      priority: promo?.priority ?? 0,
      max_uses_total: promo?.max_uses_total ?? '',
      max_uses_per_client: promo?.max_uses_per_client ?? '',
      banner_enabled: !!promo?.banner_enabled,
      banner_message: promo?.banner_message || '',
      banner_style: promo?.banner_style || 'info',
      banner_priority: promo?.banner_priority ?? 0,
      audience_type: promo?.audience?.audience_type || 'all_clients',
      audience_clients: promo?.audience?.clients || [],
      apply_to_all_services: promo?.eligibility?.apply_to_all_services ?? true,
      categories: promo?.eligibility?.categories || [],
      services: promo?.eligibility?.services || [],
      rule_type: promo?.rule?.rule_type || 'percent_off',
      percent: promo?.rule?.params?.percent ?? 10,
      nth: promo?.rule?.params?.n ?? 3,
    });
    setDialogOpen(true);
  };

  const makeUuid = () => {
    try {
      const cryptoObj = (typeof window !== 'undefined' && window.crypto);
      if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
    } catch (e) {
      // noop
    }
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  };

  const openDuplicate = (promo) => {
    setDuplicating(promo);
    setDuplicateError('');
    const currentSalon = Number(promo?.salon);
    const initialSelection = mySalons
      .map((s) => Number(s?.salon_id ?? s?.id ?? s?.pk))
      .filter((id) => id != null && !Number.isNaN(Number(id)));
    setDuplicateSalonIds(initialSelection.length > 0 ? initialSelection : (Number.isFinite(currentSalon) ? [currentSalon] : []));
    setDuplicateOverrides({
      name: '',
      description: '',
      starts_at: toDatetimeLocal(promo?.starts_at),
      ends_at: toDatetimeLocal(promo?.ends_at),
    });
    setDuplicateDialogOpen(true);
  };

  const closeDuplicateDialog = () => {
    setDuplicateDialogOpen(false);
  };

  const buildPayloadFromPromo = (promo, targetSalonId, groupId, overrides) => {
    const startsAtIso = overrides?.starts_at ? fromDatetimeLocal(overrides.starts_at) : (promo?.starts_at ?? null);
    const endsAtIso = overrides?.ends_at ? fromDatetimeLocal(overrides.ends_at) : (promo?.ends_at ?? null);
    return {
      salon: Number(targetSalonId),
      group_id: groupId ?? null,
      name: (overrides?.name ?? '').trim() ? overrides.name : (promo?.name || ''),
      description: (overrides?.description ?? '').trim() ? overrides.description : (promo?.description || ''),
      is_active: !!promo?.is_active,
      starts_at: startsAtIso,
      ends_at: endsAtIso,
      is_stackable: !!promo?.is_stackable,
      priority: promo?.priority ?? 0,
      max_uses_total: promo?.max_uses_total ?? null,
      max_uses_per_client: promo?.max_uses_per_client ?? null,
      banner_enabled: !!promo?.banner_enabled,
      banner_message: promo?.banner_message || '',
      banner_style: promo?.banner_style || 'info',
      banner_priority: promo?.banner_priority ?? 0,
      audience: {
        audience_type: promo?.audience?.audience_type || 'all_clients',
        clients: promo?.audience?.audience_type === 'selected_clients' ? (promo?.audience?.clients || []) : [],
      },
      eligibility: {
        apply_to_all_services: promo?.eligibility?.apply_to_all_services ?? true,
        categories: promo?.eligibility?.apply_to_all_services ? [] : (promo?.eligibility?.categories || []),
        services: promo?.eligibility?.apply_to_all_services ? [] : (promo?.eligibility?.services || []),
      },
      rule: {
        rule_type: promo?.rule?.rule_type || 'percent_off',
        params: promo?.rule?.params || {},
      },
    };
  };

  const handleDuplicateSubmit = async () => {
    setDuplicateError('');
    const promo = duplicating;
    if (!promo?.promotion_id) return;

    const targetIds = (Array.isArray(duplicateSalonIds) ? duplicateSalonIds : [])
      .map((x) => Number(x))
      .filter((x) => x != null && !Number.isNaN(Number(x)));

    if (targetIds.length === 0) {
      setDuplicateError(t('pro_promotions.choose_at_least_one_salon'));
      return;
    }

    try {
      const groupId = promo?.group_id || makeUuid();

      if (!promo?.group_id) {
        await updatePromotion({ promotionId: promo.promotion_id, body: { group_id: groupId } }).unwrap();
      }

      const results = await Promise.allSettled(
        targetIds.map((sid) => createPromotion(buildPayloadFromPromo(promo, sid, groupId, duplicateOverrides)).unwrap())
      );

      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        setDuplicateError(t('pro_promotions.some_promotions_not_duplicated'));
        return;
      }

      setDuplicateDialogOpen(false);
    } catch (e) {
      setDuplicateError(t('pro_promotions.duplicate_error'));
    }
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const handleNextStep = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBackStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (index) => {
    setActiveStep(index);
  };

  const buildPayload = (salonOverride, groupIdOverride) => {
    const startsAtIso = fromDatetimeLocal(form.starts_at);
    const endsAtIso = fromDatetimeLocal(form.ends_at);

    const maxTotal = form.max_uses_total === '' ? null : Number(form.max_uses_total);
    const maxPerClient = form.max_uses_per_client === '' ? null : Number(form.max_uses_per_client);

    const ruleParams = form.rule_type === 'percent_off'
      ? { percent: Number(form.percent) }
      : { n: Number(form.nth), nth_scope: 'promo_period', free_selection: 'cheapest_eligible' };

    return {
      salon: Number(salonOverride ?? salonId),
      group_id: groupIdOverride ?? null,
      name: form.name,
      description: form.description,
      is_active: !!form.is_active,
      starts_at: startsAtIso,
      ends_at: endsAtIso,
      is_stackable: !!form.is_stackable,
      priority: Number(form.priority || 0),
      max_uses_total: Number.isFinite(maxTotal) ? maxTotal : null,
      max_uses_per_client: Number.isFinite(maxPerClient) ? maxPerClient : null,
      banner_enabled: !!form.banner_enabled,
      banner_message: form.banner_message,
      banner_style: form.banner_style,
      banner_priority: Number(form.banner_priority || 0),
      audience: {
        audience_type: form.audience_type,
        clients: form.audience_type === 'selected_clients' ? (form.audience_clients || []) : [],
      },
      eligibility: {
        apply_to_all_services: !!form.apply_to_all_services,
        categories: form.apply_to_all_services ? [] : (form.categories || []),
        services: form.apply_to_all_services ? [] : (form.services || []),
      },
      rule: {
        rule_type: form.rule_type,
        params: ruleParams,
      },
    };
  };

  const buildUpdatePayload = () => {
    const { salon, group_id, ...rest } = buildPayload(editing?.salon ?? salonId, null);
    return rest;
  };

  const handleSubmit = async () => {
    setSubmitError('');

    if (!salonId) {
      setSubmitError(t('pro_promotions.create_salon_first'));
      return;
    }

    if (!form.name?.trim()) {
      setSubmitError(t('pro_promotions.name_required'));
      return;
    }

    if (form.rule_type === 'percent_off') {
      const pct = Number(form.percent);
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
        setSubmitError(t('pro_promotions.percent_range'));
        return;
      }
    }

    if (form.rule_type === 'nth_free') {
      const n = Number(form.nth);
      if (!Number.isFinite(n) || n < 2) {
        setSubmitError(t('pro_promotions.nth_minimum'));
        return;
      }
    }

    try {
      if (editing?.promotion_id) {
        const payload = buildPayload(editing?.salon ?? salonId, editing?.group_id ?? null);
        await updatePromotion({ promotionId: editing.promotion_id, body: payload }).unwrap();
      } else {
        const baseSalonIds = canChooseSalon
          ? mySalons
            .map((s) => s?.salon_id ?? s?.id ?? s?.pk)
            .filter((x) => x != null)
          : [];

        const selectedSalonIds = Array.isArray(form.selected_salon_ids) ? form.selected_salon_ids : [];
        const scope = form.apply_scope || 'current';

        const targetSalonIds = scope === 'all' && baseSalonIds.length > 0
          ? baseSalonIds
          : scope === 'selected'
            ? selectedSalonIds
              .map((x) => Number(x))
              .filter((x) => x != null && !Number.isNaN(Number(x)))
            : [Number(salonId)].filter((x) => x != null && !Number.isNaN(Number(x)));

        if (targetSalonIds.length === 0) {
          setSubmitError(t('pro_promotions.choose_at_least_one_salon'));
          return;
        }

        const groupId = targetSalonIds.length > 1 ? makeUuid() : null;

        const results = await Promise.allSettled(
          targetSalonIds.map((sid) => createPromotion(buildPayload(sid, groupId)).unwrap())
        );

        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length > 0) {
          setSubmitError(t('pro_promotions.some_promotions_not_created'));
          return;
        }
      }
      setDialogOpen(false);
    } catch (e) {
      setSubmitError(t('pro_promotions.save_error'));
    }
  };

  const handleApplyToOtherSalons = async () => {
    setSubmitError('');
    if (!editing?.promotion_id) return;
    if (!editing?.group_id) {
      setSubmitError(t('pro_promotions.no_group'));
      return;
    }

    try {
      const payload = buildUpdatePayload();
      const res = await applyToGroup({
        promotionId: editing.promotion_id,
        body: {
          ...payload,
          exclude_self: true,
        },
      }).unwrap();

      if (Array.isArray(res?.errors) && res.errors.length > 0) {
        setSubmitError(t('pro_promotions.some_promotions_not_updated'));
        return;
      }
      setDialogOpen(false);
    } catch (e) {
      setSubmitError(t('pro_promotions.apply_error'));
    }
  };

  const handleDelete = async (promo) => {
    if (!promo?.promotion_id) return;
    setDeleteTarget(promo);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.promotion_id) return;
    try {
      await deletePromotion({ promotionId: deleteTarget.promotion_id }).unwrap();
    } catch (e) {
      // noop
    } finally {
      closeDeleteConfirm();
    }
  };

  if (!salonId) {
    return (
      <Card sx={{
        mb: { xs: 1, md: 4 },
        borderRadius: { xs: 0, md: theme.borderRadius.xl },
        boxShadow: { xs: 'none', md: `0 4px 20px ${theme.colors.primary[50]}` },
        border: { xs: 'none', md: `1px solid ${theme.colors.primary[100]}` },
        backgroundColor: { xs: 'transparent', md: 'white' },
      }}>
        <CardContent sx={{ p: { xs: 1, md: 4 } }}>
          <Typography variant="h6" sx={{ color: theme.colors.primary[700], fontWeight: 'bold', mb: 1 }}>
            Promotions
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.primary[600] }}>
            Crée d'abord le salon pour pouvoir gérer ses promotions.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{
      mb: { xs: 1, md: 4 },
      borderRadius: { xs: 0, md: theme.borderRadius.xl },
      boxShadow: { xs: 'none', md: `0 4px 20px ${theme.colors.primary[50]}` },
      border: { xs: 'none', md: `1px solid ${theme.colors.primary[100]}` },
      backgroundColor: { xs: 'transparent', md: 'white' },
    }}>
      <CardContent sx={{ p: { xs: 1, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ color: theme.colors.primary[700], fontWeight: 'bold' }}>
            {t('pro_promotions.title')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              backgroundColor: theme.colors.primary[600],
              '&:hover': { backgroundColor: theme.colors.primary[700] },
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            {t('common.add')}
          </Button>
        </Box>

        {error ? (
          <Alert severity="error">{t('pro_promotions.load_error')}</Alert>
        ) : null}

        {(categoriesError || servicesError || clientsError) ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('pro_promotions.lists_error')}
          </Alert>
        ) : null}

        {isLoading ? (
          <Typography sx={{ color: theme.colors.gray[600] }}>{t('common.loading')}</Typography>
        ) : salonPromotions.length === 0 ? (
          <Typography sx={{ color: theme.colors.gray[600] }}>{t('pro_promotions.no_promotions')}</Typography>
        ) : (
          <Grid container spacing={3}>
            {salonPromotions.map((p) => (
              <Grid item xs={12} sm={6} md={4} key={p.promotion_id}>
                <Card
                  sx={{
                    borderRadius: 3.5,
                    border: `1px solid ${theme.colors.primary[100]}`,
                    backgroundColor: 'white',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'visible',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: `0 15px 30px ${theme.colors.primary[100]}`,
                      borderColor: theme.colors.primary[400]
                    }
                  }}
                >
                  {p.is_active && (
                    <Box sx={{
                      position: 'absolute',
                      top: -8,
                      right: 16,
                      backgroundColor: theme.colors.success[500],
                      color: 'white',
                      px: 1.5,
                      py: 0.3,
                      borderRadius: 1.5,
                      fontSize: '0.65rem',
                      fontWeight: 900,
                      boxShadow: `0 4px 8px ${theme.colors.success[200]}`,
                      zIndex: 1
                    }}>
                      {t('pro_promotions.active').toUpperCase()}
                    </Box>
                  )}
                  <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, color: theme.colors.gray[900], lineHeight: 1.1, fontSize: '1.05rem' }} noWrap title={p.name}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.colors.gray[400], mt: 0.2, display: 'block', letterSpacing: 0.5 }}>
                          ID: #{p.promotion_id.toString().split('-')[0]}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: theme.colors.primary[600], fontSize: '1.25rem', lineHeight: 1 }}>
                          {p.rule?.rule_type === 'percent_off' ? `-${p.rule?.params?.percent}%` : `N°${p.rule?.params?.n}`}
                        </Typography>
                        {p.rule?.rule_type === 'nth_free' && <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: theme.colors.primary[400], textTransform: 'uppercase' }}>OFFERT</Typography>}
                      </Box>
                    </Box>

                    {p.banner_enabled && (
                      <Box sx={{
                        mb: 2,
                        p: 1.2,
                        borderRadius: 2,
                        backgroundColor: theme.colors.gray[50],
                        borderLeft: `3px solid ${theme.colors.primary[400]}`,
                        overflow: 'hidden'
                      }}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.colors.gray[600], fontSize: '0.8rem', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          "{p.banner_message}"
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 2, mt: 'auto' }}>
                      <Chip
                        size="small"
                        label={p.is_stackable ? t('pro_promotions.stackable') : t('pro_promotions.not_stackable')}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700, borderColor: p.is_stackable ? theme.colors.primary[200] : theme.colors.gray[200] }}
                      />
                      {p.eligibility?.apply_to_all_services ? (
                        <Chip size="small" label={t('pro_promotions.global')} sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700, backgroundColor: theme.colors.primary[50], color: theme.colors.primary[700] }} />
                      ) : (
                        <Chip size="small" label={t('pro_promotions.targeted')} sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700, backgroundColor: theme.colors.info[50], color: theme.colors.info[700] }} />
                      )}
                      {p.starts_at && (
                        <Chip
                          size="small"
                          label={new Date(p.starts_at).toLocaleDateString([], { day: 'numeric', month: 'short' })}
                          sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700, backgroundColor: theme.colors.gray[100] }}
                        />
                      )}
                    </Box>

                    <Divider sx={{ mb: 1.5, borderStyle: 'dotted' }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 0.2 }}>
                        <Button
                          size="small"
                          onClick={() => openEdit(p)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            color: theme.colors.primary[600],
                            '&:hover': { backgroundColor: theme.colors.primary[50] }
                          }}
                          startIcon={<EditIcon sx={{ fontSize: '1rem !important' }} />}
                        >
                          {t('common.manage')}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => openDuplicate(p)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            color: theme.colors.gray[600],
                            '&:hover': { backgroundColor: theme.colors.gray[50] }
                          }}
                          startIcon={<ContentCopyIcon sx={{ fontSize: '1rem !important' }} />}
                        >
                          {t('common.duplicate')}
                        </Button>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(p)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          minWidth: 'auto',
                          opacity: 0.7,
                          '&:hover': { opacity: 1 }
                        }}
                        startIcon={<DeleteIcon sx={{ fontSize: '1rem !important' }} />}
                      >
                        {t('common.delete')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog open={duplicateDialogOpen} onClose={closeDuplicateDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{
            fontWeight: 900,
            p: 3,
            color: theme.colors.gray[900],
            borderBottom: `1px solid ${theme.colors.border.light}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 2,
              backgroundColor: theme.colors.primary[50], color: theme.colors.primary[600],
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <ContentCopyIcon />
            </Box>
            {t('pro_promotions.duplicate_title')}
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            {duplicateError ? <Alert severity="error" sx={{ mb: 3 }}>{duplicateError}</Alert> : null}

            <Typography variant="subtitle2" sx={{ color: theme.colors.gray[700], fontWeight: 800, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('pro_promotions.target_salons')}
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('pro_promotions.select_salons')}</InputLabel>
              <Select
                label={t('pro_promotions.select_salons')}
                multiple
                value={duplicateSalonIds}
                onChange={(e) => setDuplicateSalonIds((Array.isArray(e.target.value) ? e.target.value : []).map((v) => Number(v)))}
                disabled={!canChooseSalon || !hasAnySalon}
                sx={{ borderRadius: 3 }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const nid = Number(id);
                      const s = mySalons.find((x) => Number(x?.salon_id ?? x?.id ?? x?.pk) === nid);
                      return <Chip key={id} label={s?.name || `Salon #${nid}`} size="small" sx={{ fontWeight: 600 }} />;
                    })}
                  </Box>
                )}
              >
                {mySalons.map((s) => {
                  const id = Number(s?.salon_id ?? s?.id ?? s?.pk);
                  return <MenuItem key={id} value={id}>{s?.name || `Salon #${id}`}</MenuItem>;
                })}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ color: theme.colors.gray[700], fontWeight: 800, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('pro_promotions.quick_changes')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label={t('pro_promotions.new_name_placeholder')}
                value={duplicateOverrides.name}
                onChange={(e) => setDuplicateOverrides((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
                InputProps={{ sx: { borderRadius: 3 } }}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('pro_promotions.start_date')}
                    type="datetime-local"
                    value={duplicateOverrides.starts_at}
                    onChange={(e) => setDuplicateOverrides((prev) => ({ ...prev, starts_at: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('pro_promotions.end_date')}
                    type="datetime-local"
                    value={duplicateOverrides.ends_at}
                    onChange={(e) => setDuplicateOverrides((prev) => ({ ...prev, ends_at: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.colors.border.light}`, backgroundColor: theme.colors.gray[50] }}>
            <Button onClick={closeDuplicateDialog} sx={{ textTransform: 'none', fontWeight: 700, color: theme.colors.gray[600] }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDuplicateSubmit}
              variant="contained"
              disabled={isCreating || isUpdating}
              sx={{
                backgroundColor: theme.colors.primary[600],
                textTransform: 'none',
                fontWeight: 800,
                px: 4, borderRadius: 2.5,
                boxShadow: `0 4px 14px ${theme.colors.primary[100]}`
              }}
            >
              {t('pro_promotions.duplicate_now')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteConfirmOpen}
          onClose={closeDeleteConfirm}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogContent sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: '50%',
              backgroundColor: theme.colors.error[50], color: theme.colors.error[500],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <DeleteIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: theme.colors.gray[900], mb: 1 }}>
              {t('pro_promotions.delete_confirm_title')}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.colors.gray[600], mb: 3 }}>
              {t('pro_promotions.delete_confirm_message', { name: deleteTarget?.name })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                onClick={closeDeleteConfirm}
                variant="outlined"
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2.5, borderColor: theme.colors.gray[300], color: theme.colors.gray[700] }}
              >
                {t('common.keep')}
              </Button>
              <Button
                fullWidth
                onClick={confirmDelete}
                color="error"
                variant="contained"
                sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2.5, boxShadow: `0 4px 14px ${theme.colors.error[100]}` }}
              >
                {t('common.delete')}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        <Dialog
          open={dialogOpen}
          onClose={closeDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 5,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              backgroundColor: '#ffffff'
            }
          }}
        >
          <DialogTitle
            sx={{
              p: 3,
              fontWeight: 900,
              fontSize: '1.25rem',
              color: theme.colors.gray[900],
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: `1px solid ${theme.colors.border.light}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: theme.colors.primary[50],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.primary[600]
              }}>
                {editing ? <EditIcon /> : <AddIcon />}
              </Box>
              {editing ? t('pro_promotions.edit_promotion') : t('pro_promotions.new_promotion')}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 5 }, py: 4 }}>
            {submitError ? <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert> : null}

            <Box
              sx={{
                mb: 3,
                p: { xs: 1.5, sm: 3 },
                borderRadius: 4,
                background: `linear-gradient(135deg, ${theme.colors.primary[50]} 0%, #ffffff 100%)`,
                border: `1px solid ${theme.colors.primary[100]}`,
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  justifyContent: 'center',
                  rowGap: 4,
                  '& .MuiStep-root': {
                    flex: { xs: '0 0 33.33%', sm: '1' },
                    px: { xs: 0, sm: 1 },
                    minWidth: 0,
                  },
                  '& .MuiStepConnector-root': {
                    display: { xs: 'none', sm: 'block' },
                  },
                  '& .MuiStepConnector-line': {
                    borderColor: theme.colors.primary[100],
                    borderTopWidth: 2,
                  },
                  '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                    borderColor: theme.colors.primary[500],
                  },
                  '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                    borderColor: theme.colors.primary[500],
                  }
                }}
              >
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel
                      onClick={() => handleStepClick(index)}
                      StepIconComponent={() => (
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: index <= activeStep ? theme.colors.primary[500] : theme.colors.gray[100],
                            color: index <= activeStep ? 'white' : theme.colors.gray[400],
                            boxShadow: index === activeStep ? `0 0 0 4px ${theme.colors.primary[100]}` : 'none',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            fontSize: '1.2rem'
                          }}
                        >
                          {index < activeStep ? '✓' : step.icon}
                        </Box>
                      )}
                      sx={{
                        cursor: 'pointer',
                        '& .MuiStepLabel-label': {
                          mt: 1,
                          fontWeight: index === activeStep ? 800 : 500,
                          color: index === activeStep ? theme.colors.primary[700] : theme.colors.gray[500],
                          fontSize: '0.85rem',
                          transition: 'all 0.3s ease',
                        },
                      }}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            <Box sx={{ width: '100%' }}>
              {activeStep === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                  {!editing && (
                    <Box>
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 4,
                          border: `1px solid ${theme.colors.primary[100]}`,
                          backgroundColor: theme.colors.primary[50],
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ color: theme.colors.primary[700], fontWeight: 800, mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                          {t('pro_promotions.promotion_distribution')}
                        </Typography>
                        <FormControl component="fieldset" fullWidth>
                          <RadioGroup
                            row
                            value={form.apply_scope}
                            onChange={(e) => {
                              const value = e.target.value;
                              setForm((p) => ({
                                ...p,
                                apply_scope: value,
                                selected_salon_ids: value === 'current' && salonId ? [Number(salonId)] : p.selected_salon_ids,
                              }));
                            }}
                            sx={{ gap: { xs: 1, sm: 3 } }}
                          >
                            <FormControlLabel
                              value="current"
                              control={<Radio size="small" />}
                              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{t('pro_promotions.only_this_salon')}</Typography>}
                            />
                            <FormControlLabel
                              value="all"
                              control={<Radio size="small" />}
                              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{t('pro_promotions.all_my_salons')}</Typography>}
                              disabled={!canChooseSalon}
                            />
                            <FormControlLabel
                              value="selected"
                              control={<Radio size="small" />}
                              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{t('pro_promotions.salon_selection')}</Typography>}
                              disabled={!canChooseSalon}
                            />
                          </RadioGroup>
                        </FormControl>

                        {form.apply_scope === 'selected' && (
                          <Box sx={{ mt: 2 }}>
                            <FormControl fullWidth>
                              <InputLabel>{t('pro_promotions.choose_salons')}</InputLabel>
                              <Select
                                label={t('pro_promotions.choose_salons')}
                                multiple
                                value={form.selected_salon_ids}
                                onChange={(e) => setForm((prev) => ({ ...prev, selected_salon_ids: e.target.value }))}
                                disabled={!hasAnySalon || !canChooseSalon}
                                sx={{ borderRadius: 3, backgroundColor: 'white' }}
                                renderValue={(selected) => (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((id) => {
                                      const s = (hasAnySalon ? mySalons : [{ salon_id: Number(salonId), name: `Salon #${salonId}` }])
                                        .find((x) => (x?.salon_id ?? x?.id ?? x?.pk) === id);
                                      return (
                                        <Chip
                                          key={id}
                                          label={s?.name || `Salon #${id}`}
                                          size="small"
                                          sx={{ borderRadius: 1.5, fontWeight: 600 }}
                                        />
                                      );
                                    })}
                                  </Box>
                                )}
                              >
                                {(hasAnySalon ? mySalons : [{ salon_id: Number(salonId), name: `Salon #${salonId}` }]).map((s) => {
                                  const id = s?.salon_id ?? s?.id ?? s?.pk;
                                  const label = s?.name || `Salon #${id}`;
                                  return (
                                    <MenuItem key={id} value={id}>
                                      {label}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Box>
                    <Box sx={{
                      p: 3,
                      borderRadius: 4,
                      border: `1px solid ${theme.colors.gray[200]}`,
                      backgroundColor: 'white'
                    }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" sx={{ color: theme.colors.primary[500], fontWeight: 800, letterSpacing: 1.5 }}>
                          {t('pro_promotions.identification')}
                        </Typography>
                        <Typography variant="h6" sx={{ color: theme.colors.gray[800], fontWeight: 700 }}>
                          {t('pro_promotions.offer_details')}
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                          <TextField
                            label={t('pro_promotions.promotion_name')}
                            placeholder={t('pro_promotions.promotion_name_placeholder')}
                            value={form.name}
                            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                            fullWidth
                            variant="outlined"
                            InputProps={{
                              sx: { borderRadius: 3, fontWeight: 600 }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{
                            px: 2,
                            borderRadius: 3,
                            border: `1px solid ${form.is_active ? theme.colors.success[200] : theme.colors.gray[200]}`,
                            backgroundColor: form.is_active ? theme.colors.success[50] : theme.colors.gray[50],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: '56px',
                            boxSizing: 'border-box'
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: form.is_active ? theme.colors.success[700] : theme.colors.gray[700] }}>
                              {form.is_active ? t('pro_promotions.active') : t('pro_promotions.inactive')}
                            </Typography>
                            <Switch
                              size="small"
                              checked={!!form.is_active}
                              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                              color="success"
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label={t('pro_promotions.description_optional')}
                            placeholder={t('pro_promotions.description_placeholder')}
                            value={form.description}
                            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                            fullWidth
                            multiline
                            minRows={2}
                            variant="outlined"
                            InputProps={{
                              sx: { borderRadius: 3 }
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 3 }}>
                        <Chip label={t('pro_promotions.validity_period')} size="small" sx={{ color: theme.colors.gray[400], fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }} />
                      </Divider>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label={t('pro_promotions.start_date')}
                            type="datetime-local"
                            value={form.starts_at}
                            onChange={(e) => setForm((prev) => ({ ...prev, starts_at: e.target.value }))}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ sx: { borderRadius: 3 } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label={t('pro_promotions.end_date')}
                            type="datetime-local"
                            value={form.ends_at}
                            onChange={(e) => setForm((prev) => ({ ...prev, ends_at: e.target.value }))}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ sx: { borderRadius: 3 } }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              ) : null}

              {activeStep === 1 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                  <Box>
                    <Box sx={{
                      p: 3,
                      borderRadius: 4,
                      border: `1px solid ${theme.colors.gray[200]}`,
                      backgroundColor: 'white'
                    }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" sx={{ color: theme.colors.primary[500], fontWeight: 800, letterSpacing: 1.5, display: 'block' }}>
                          {t('pro_promotions.benefit')}
                        </Typography>
                        <Typography variant="h6" sx={{ color: theme.colors.gray[800], fontWeight: 700 }}>
                          {t('pro_promotions.configure_benefit')}
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>{t('pro_promotions.discount_type')}</InputLabel>
                            <Select
                              label={t('pro_promotions.discount_type')}
                              value={form.rule_type}
                              onChange={(e) => setForm((prev) => ({ ...prev, rule_type: e.target.value }))}
                              sx={{ borderRadius: 3 }}
                            >
                              <MenuItem value="percent_off">🎟️ {t('pro_promotions.percent_off')}</MenuItem>
                              <MenuItem value="nth_free">✨ {t('pro_promotions.nth_free')}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          {form.rule_type === 'percent_off' ? (
                            <TextField
                              label={t('pro_promotions.percent_value')}
                              type="number"
                              placeholder={t('pro_promotions.percent_placeholder')}
                              value={form.percent}
                              onChange={(e) => setForm((prev) => ({ ...prev, percent: Number(e.target.value) }))}
                              fullWidth
                              InputProps={{
                                sx: { borderRadius: 3, fontWeight: 800, fontSize: '1.2rem' },
                                endAdornment: <Typography sx={{ ml: 1, fontWeight: 800 }}>%</Typography>
                              }}
                            />
                          ) : (
                            <TextField
                              label={t('pro_promotions.number_n')}
                              type="number"
                              placeholder={t('pro_promotions.number_placeholder')}
                              value={form.nth}
                              onChange={(e) => setForm((prev) => ({ ...prev, nth: Number(e.target.value) }))}
                              fullWidth
                              InputProps={{
                                sx: { borderRadius: 3, fontWeight: 800, fontSize: '1.2rem' }
                              }}
                            />
                          )}
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 4 }}>
                        <Chip label={t('pro_promotions.usage_conditions')} size="small" sx={{ color: theme.colors.gray[400], fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }} />
                      </Divider>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{
                            px: 2,
                            borderRadius: 3,
                            border: `1px solid ${theme.colors.gray[200]}`,
                            backgroundColor: theme.colors.gray[50],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: '56px',
                            boxSizing: 'border-box'
                          }}>
                            <Typography sx={{ fontWeight: 700, color: theme.colors.gray[700] }}>
                              {t('pro_promotions.stackable_allowed')}
                            </Typography>
                            <Switch
                              size="small"
                              checked={!!form.is_stackable}
                              onChange={(e) => setForm((p) => ({ ...p, is_stackable: e.target.checked }))}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('pro_promotions.priority')}
                            type="number"
                            value={form.priority}
                            onChange={(e) => setForm((prev) => ({ ...prev, priority: Number(e.target.value) }))}
                            fullWidth
                            InputProps={{ sx: { borderRadius: 3 } }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('pro_promotions.salon_limit')}
                            type="number"
                            placeholder={t('pro_promotions.no_limit')}
                            value={form.max_uses_total}
                            onChange={(e) => setForm((prev) => ({ ...prev, max_uses_total: e.target.value }))}
                            fullWidth
                            InputProps={{ sx: { borderRadius: 3 } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('pro_promotions.client_limit')}
                            type="number"
                            placeholder={t('pro_promotions.no_limit')}
                            value={form.max_uses_per_client}
                            onChange={(e) => setForm((prev) => ({ ...prev, max_uses_per_client: e.target.value }))}
                            fullWidth
                            InputProps={{ sx: { borderRadius: 3 } }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              ) : null}

              {activeStep === 2 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                  <Box>
                    <Box sx={{
                      p: 3,
                      borderRadius: 4,
                      border: `1px solid ${theme.colors.gray[200]}`,
                      backgroundColor: 'white'
                    }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" sx={{ color: theme.colors.primary[500], fontWeight: 800, letterSpacing: 1.5, display: 'block' }}>
                          {t('pro_promotions.service_targeting')}
                        </Typography>
                        <Typography variant="h6" sx={{ color: theme.colors.gray[800], fontWeight: 700 }}>
                          {t('pro_promotions.eligible_services')}
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Box sx={{
                            p: 2,
                            borderRadius: 4,
                            border: `1px solid ${form.apply_to_all_services ? theme.colors.primary[200] : theme.colors.gray[200]}`,
                            backgroundColor: form.apply_to_all_services ? theme.colors.primary[50] : 'transparent',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: '56px',
                            boxSizing: 'border-box'
                          }}>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: theme.colors.gray[800], fontSize: '0.9rem' }}>{t('pro_promotions.apply_all_salon')}</Typography>
                            </Box>
                            <Switch
                              checked={!!form.apply_to_all_services}
                              onChange={(e) => setForm((p) => ({ ...p, apply_to_all_services: e.target.checked }))}
                              size="small"
                              color="primary"
                            />
                          </Box>
                        </Grid>

                        {!form.apply_to_all_services && (
                          <>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>{t('pro_promotions.by_categories')}</InputLabel>
                                <Select
                                  label={t('pro_promotions.by_categories')}
                                  multiple
                                  value={form.categories}
                                  onChange={(e) => setForm((prev) => ({ ...prev, categories: e.target.value }))}
                                  sx={{ borderRadius: 3 }}
                                  renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {selected.map((value) => {
                                        const cat = effectiveCategories.find((c) => (c.category_id ?? c.id ?? c.pk) === value);
                                        return (
                                          <Chip
                                            key={value}
                                            label={cat?.name || value}
                                            size="small"
                                            sx={{ borderRadius: 1.5, fontWeight: 600, backgroundColor: theme.colors.primary[100], color: theme.colors.primary[800] }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  )}
                                >
                                  {effectiveCategories.map((c) => (
                                    <MenuItem key={c.category_id ?? c.id ?? c.pk} value={c.category_id ?? c.id ?? c.pk}>
                                      {c.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                              <FormControl fullWidth>
                                <InputLabel>{t('pro_promotions.specific_services')}</InputLabel>
                                <Select
                                  label={t('pro_promotions.specific_services')}
                                  multiple
                                  value={form.services}
                                  onChange={(e) => setForm((prev) => ({ ...prev, services: e.target.value }))}
                                  sx={{ borderRadius: 3 }}
                                  renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {selected.map((value) => {
                                        const srv = effectiveServices.find((s) => (s.service_id ?? s.id ?? s.pk) === value);
                                        return (
                                          <Chip
                                            key={value}
                                            label={srv?.name || value}
                                            size="small"
                                            sx={{ borderRadius: 1.5, fontWeight: 600, backgroundColor: theme.colors.primary[100], color: theme.colors.primary[800] }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  )}
                                >
                                  {effectiveServices.map((s) => (
                                    <MenuItem key={s.service_id ?? s.id ?? s.pk} value={s.service_id ?? s.id ?? s.pk}>
                                      {s.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              ) : null}

              {activeStep === 3 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                  <Box>
                    <Box sx={{
                      p: 3,
                      borderRadius: 4,
                      border: `1px solid ${theme.colors.gray[200]}`,
                      backgroundColor: 'white'
                    }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" sx={{ color: theme.colors.primary[500], fontWeight: 800, letterSpacing: 1.5, display: 'block' }}>
                          {t('pro_promotions.client_targeting')}
                        </Typography>
                        <Typography variant="h6" sx={{ color: theme.colors.gray[800], fontWeight: 700 }}>
                          {t('pro_promotions.who_can_benefit')}
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Box
                            onClick={() => setForm(p => ({ ...p, audience_type: 'all_clients' }))}
                            sx={{
                              p: 2.5,
                              cursor: 'pointer',
                              borderRadius: 4,
                              border: `2px solid ${form.audience_type === 'all_clients' ? theme.colors.primary[500] : theme.colors.gray[200]}`,
                              backgroundColor: form.audience_type === 'all_clients' ? theme.colors.primary[50] : 'transparent',
                              transition: 'all 0.2s ease',
                              '&:hover': { borderColor: theme.colors.primary[300] }
                            }}
                          >
                            <Typography sx={{ fontWeight: 800, color: theme.colors.gray[800] }}>🌍 {t('pro_promotions.all_clients')}</Typography>
                            <Typography variant="body2" sx={{ color: theme.colors.gray[500], mt: 0.5 }}>{t('pro_promotions.public_offer')}</Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Box
                            onClick={() => setForm(p => ({ ...p, audience_type: 'selected_clients' }))}
                            sx={{
                              p: 2.5,
                              cursor: 'pointer',
                              borderRadius: 4,
                              border: `2px solid ${form.audience_type === 'selected_clients' ? theme.colors.primary[500] : theme.colors.gray[200]}`,
                              backgroundColor: form.audience_type === 'selected_clients' ? theme.colors.primary[50] : 'transparent',
                              transition: 'all 0.2s ease',
                              '&:hover': { borderColor: theme.colors.primary[300] }
                            }}
                          >
                            <Typography sx={{ fontWeight: 800, color: theme.colors.gray[800] }}>🎯 {t('pro_promotions.targeted_selection')}</Typography>
                            <Typography variant="body2" sx={{ color: theme.colors.gray[500], mt: 0.5 }}>{t('pro_promotions.targeted_offer')}</Typography>
                          </Box>
                        </Grid>

                        {form.audience_type === 'selected_clients' && (
                          <Grid item xs={12}>
                            <FormControl fullWidth disabled={clientsLoading}>
                              <InputLabel>{t('pro_promotions.select_clients')}</InputLabel>
                              <Select
                                label={t('pro_promotions.select_clients')}
                                multiple
                                value={form.audience_clients}
                                onChange={(e) => setForm((prev) => ({ ...prev, audience_clients: e.target.value }))}
                                sx={{ borderRadius: 3 }}
                                renderValue={(selected) => (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((id) => {
                                      const u = clients.find((c) => (c.user_id ?? c.id ?? c.pk) === id);
                                      const label = u ? (`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email) : id;
                                      return (
                                        <Chip
                                          key={id}
                                          label={label}
                                          size="small"
                                          sx={{ borderRadius: 1.5, fontWeight: 600, backgroundColor: theme.colors.primary[100], color: theme.colors.primary[800] }}
                                        />
                                      );
                                    })}
                                  </Box>
                                )}
                              >
                                {clients.map((u) => {
                                  const id = u.user_id ?? u.id ?? u.pk;
                                  const label = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                                  return (
                                    <MenuItem key={id} value={id}>
                                      {label}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              ) : null}

              {activeStep === 4 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                  <Box>
                    <Box sx={{ p: 3, borderRadius: 4, border: `1px solid ${theme.colors.gray[200]}`, backgroundColor: 'white' }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" sx={{ color: theme.colors.primary[500], fontWeight: 800, letterSpacing: 1.5, display: 'block' }}>
                          {t('pro_promotions.marketing_visibility')}
                        </Typography>
                        <Typography variant="h6" sx={{ color: theme.colors.gray[800], fontWeight: 700 }}>
                          {t('pro_promotions.how_display')}
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Box sx={{
                            px: 2,
                            borderRadius: 4,
                            border: `1px solid ${form.banner_enabled ? theme.colors.secondary[300] : theme.colors.gray[200]}`,
                            backgroundColor: form.banner_enabled ? theme.colors.secondary[50] : 'transparent',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: '56px',
                            boxSizing: 'border-box'
                          }}>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: theme.colors.gray[800], fontSize: '0.9rem' }}>{t('pro_promotions.promotional_banner')}</Typography>
                            </Box>
                            <Switch
                              checked={!!form.banner_enabled}
                              onChange={(e) => setForm((p) => ({ ...p, banner_enabled: e.target.checked }))}
                              size="small"
                              color="warning"
                            />
                          </Box>
                        </Grid>

                        {form.banner_enabled ? (
                          <>
                            <Grid item xs={12}>
                              <TextField
                                label={t('pro_promotions.banner_message')}
                                placeholder={t('pro_promotions.banner_message_placeholder')}
                                value={form.banner_message}
                                onChange={(e) => setForm((prev) => ({ ...prev, banner_message: e.target.value }))}
                                fullWidth
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>{t('pro_promotions.visual_style')}</InputLabel>
                                <Select
                                  label={t('pro_promotions.visual_style')}
                                  value={form.banner_style}
                                  onChange={(e) => setForm((prev) => ({ ...prev, banner_style: e.target.value }))}
                                  sx={{ borderRadius: 3 }}
                                >
                                  <MenuItem value="info">💎 {t('pro_promotions.style_info')}</MenuItem>
                                  <MenuItem value="success">✅ {t('pro_promotions.style_success')}</MenuItem>
                                  <MenuItem value="warning">🔥 {t('pro_promotions.style_warning')}</MenuItem>
                                  <MenuItem value="festive">✨ {t('pro_promotions.style_festive')}</MenuItem>
                                  <MenuItem value="love">💖 {t('pro_promotions.style_love')}</MenuItem>
                                  <MenuItem value="premium">🌟 {t('pro_promotions.style_premium')}</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                label={t('pro_promotions.display_priority')}
                                type="number"
                                value={form.banner_priority}
                                onChange={(e) => setForm((prev) => ({ ...prev, banner_priority: Number(e.target.value) }))}
                                fullWidth
                                helperText={t('pro_promotions.priority_helper')}
                                InputProps={{ sx: { borderRadius: 3 } }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Box sx={{ mt: 2, p: 2, borderRadius: 3, backgroundColor: theme.colors.gray[50], border: `1px dashed ${theme.colors.gray[300]}` }}>
                                <Typography variant="caption" sx={{ color: theme.colors.gray[500], fontWeight: 800, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                                  {t('pro_promotions.banner_preview')}
                                </Typography>
                                <PromotionBanner
                                  message={form.banner_message || t('pro_promotions.banner_message_placeholder')}
                                  style={form.banner_style}
                                  dense
                                />
                              </Box>
                            </Grid>
                          </>
                        ) : null}
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.colors.border.light}`, backgroundColor: theme.colors.gray[50] }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, order: { xs: 2, sm: 1 } }}>
                <Button
                  onClick={closeDialog}
                  variant="text"
                  sx={{ textTransform: 'none', fontWeight: 700, color: theme.colors.gray[600] }}
                >
                  {t('common.cancel')}
                </Button>
                {editing?.promotion_id && editing?.group_id ? (
                  <Button
                    onClick={handleApplyToOtherSalons}
                    variant="outlined"
                    disabled={isApplyingToGroup || isUpdating}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2.5 }}
                  >
                    {t('pro_promotions.update_everywhere')}
                  </Button>
                ) : null}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  order: { xs: 1, sm: 2 },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                <Button
                  onClick={handleBackStep}
                  disabled={activeStep === 0}
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2.5,
                    px: 3,
                    borderColor: theme.colors.gray[300],
                    color: theme.colors.gray[700]
                  }}
                >
                  {t('common.previous')}
                </Button>
                {activeStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNextStep}
                    variant="contained"
                    sx={{
                      backgroundColor: theme.colors.primary[600],
                      '&:hover': { backgroundColor: theme.colors.primary[700] },
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 2.5,
                      px: 4,
                      boxShadow: `0 4px 14px 0 ${theme.colors.primary[200]}`
                    }}
                  >
                    {t('common.next')}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isCreating || isUpdating || isApplyingToGroup}
                    sx={{
                      backgroundColor: theme.colors.primary[600],
                      '&:hover': { backgroundColor: theme.colors.primary[700] },
                      textTransform: 'none',
                      fontWeight: 800,
                      borderRadius: 2.5,
                      px: 4,
                      boxShadow: `0 4px 14px 0 ${theme.colors.primary[200]}`
                    }}
                  >
                    {editing ? t('pro_promotions.save_changes') : t('pro_promotions.create_promotion')}
                  </Button>
                )}
              </Box>
            </Box>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card >
  );
};

export default SalonPromotions;
